import React from "react";
import {
  Download,
  TrendingUp,
  Calendar,
  PieChart,
  BarChart3,
  Target,
  Building,
  DollarSign,
  CreditCard,
  Users,
  Settings,
  Loader,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from "recharts";
import { useRevenusPeriod, useExportRevenus } from "./hooks/useRevenus";
import { Periode } from "./utils/types";
import { Layout } from "../../../layouts/DashboardLayout";

const RevenusPage: React.FC = () => {
  const { periodeActive, setPeriodeActive, data, isLoading, error } = useRevenusPeriod();
  const exportMutation = useExportRevenus();

  const periodes: { key: Periode; label: string; icon: React.ReactNode }[] = [
    { key: "journalier", label: "Journalier", icon: <Calendar size={16} /> },
    { key: "hebdomadaire", label: "Hebdomadaire", icon: <BarChart3 size={16} /> },
    { key: "mensuel", label: "Mensuel", icon: <TrendingUp size={16} /> },
    { key: "trimestriel", label: "Trimestriel", icon: <PieChart size={16} /> },
    { key: "semestriel", label: "Semestriel", icon: <Building size={16} /> },
    { key: "annuel", label: "Annuel", icon: <Target size={16} /> },
  ];

  const handleExport = (format: "pdf" | "excel") => {
    exportMutation.mutate({
      format,
      periode: periodeActive,
      dateDebut: "2024-01-01",
      dateFin: "2024-01-31",
      sections: ["benefices", "marges", "partenaires", "services"],
    });
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-2">Erreur de chargement</div>
          <div className="text-gray-600">Impossible de charger les données des revenus</div>
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des données...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const { metriquesPrincipales, beneficesNets, margeChange, coutsPartenaires, evolutionBenefice, profitsParService } =
    data;

  return (
    <Layout>
      <div className="p-2 space-y-6">
        {/* En-tête avec export */}

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <DollarSign className="w-8 h-8 mt-2 text-green-600" />
                Tableau de Bord Financier
              </h1>
              <p className="text-gray-600 mt-1">Analyse complète des revenus et bénéfices - Vue {periodeActive}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleExport("excel")}
              disabled={exportMutation.isPending}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              {exportMutation.isPending ? <Loader className="animate-spin" size={18} /> : <Download size={18} />}
              <span>Export Excel</span>
            </button>
            <button
              onClick={() => handleExport("pdf")}
              disabled={exportMutation.isPending}
              className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              {exportMutation.isPending ? <Loader className="animate-spin" size={18} /> : <Download size={18} />}
              <span>Export PDF</span>
            </button>
          </div>
        </div>

        {/* Navigation périodes */}
        <div className="bg-white rounded-lg border border-gray-200 p-2">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {periodes.map((periode) => (
              <button
                key={periode.key}
                onClick={() => setPeriodeActive(periode.key)}
                disabled={isLoading}
                className={`flex items-center gap-2 p-3 rounded-md transition-all duration-200 ${
                  periodeActive === periode.key
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "text-gray-600 hover:bg-gray-50 border border-transparent"
                } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {periode.icon}
                <span className="text-sm font-medium">{periode.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Métrique principale */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">Revenu {periodeActive}</div>
              <div className="text-2xl font-bold text-gray-900">
                {metriquesPrincipales.revenuTotal.toLocaleString()} CAD
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">Bénéfice Net</div>
              <div className="text-2xl font-bold text-green-600">
                {metriquesPrincipales.beneficeNetTotal.toLocaleString()} CAD
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">Marge Moyenne</div>
              <div className="text-2xl font-bold text-blue-600">{metriquesPrincipales.margeMoyenne}%</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">Évolution</div>
              <div
                className={`text-2xl font-bold ${metriquesPrincipales.evolutionPourcentage >= 0 ? "text-green-600" : "text-red-600"}`}
              >
                {metriquesPrincipales.evolutionPourcentage >= 0 ? "+" : ""}
                {metriquesPrincipales.evolutionPourcentage}%
              </div>
            </div>
          </div>
        </div>

        {/* Section 1: Bénéfices Nets */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="text-green-600" size={20} />
              <h2 className="text-lg font-semibold text-gray-900">Bénéfices Nets</h2>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Bénéfice net total</span>
                <div className="text-right">
                  <div className="text-xl font-bold text-gray-900">{beneficesNets.total.toLocaleString()} CAD</div>
                  <div
                    className={`text-sm ${beneficesNets.evolutionMoisPrecedent >= 0 ? "text-green-600" : "text-red-600"}`}
                  >
                    {beneficesNets.evolutionMoisPrecedent >= 0 ? "↗" : "↘"}
                    {Math.abs(beneficesNets.evolutionMoisPrecedent)}% vs période précédente
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <div className="text-sm text-gray-600">Commissions totales</div>
                  <div className="font-semibold text-gray-900">
                    {beneficesNets.breakdown.commissionsTotales.toLocaleString()} CAD
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Marge sur change</div>
                  <div className="font-semibold text-gray-900">
                    {beneficesNets.breakdown.margeChange.toLocaleString()} CAD
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Coûts des payouts</div>
                  <div className="font-semibold text-gray-900">
                    {beneficesNets.breakdown.coutsPayouts.toLocaleString()} CAD
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Coûts fixes</div>
                  <div className="font-semibold text-gray-900">
                    {beneficesNets.breakdown.coutsFixes.toLocaleString()} CAD
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Marge sur Taux de Change */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="text-blue-600" size={20} />
              <h2 className="text-lg font-semibold text-gray-900">Marge sur Taux de Change</h2>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Marge moyenne</div>
                  <div className="text-xl font-bold text-gray-900">{margeChange.margeMoyenne}%</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Profit total</div>
                  <div className="text-xl font-bold text-gray-900">{margeChange.profitTotal.toLocaleString()} CAD</div>
                </div>
              </div>

              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={margeChange.evolution}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="periode" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => [`${value}%`, "Marge"]}
                      labelFormatter={(label) => `Période: ${label}`}
                    />
                    <Area type="monotone" dataKey="marge" stroke="#3b82f6" fill="url(#colorMarge)" strokeWidth={2} />
                    <defs>
                      <linearGradient id="colorMarge" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3 & 4: Coûts Partenaires et Évolution Bénéfice */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Coûts par Partenaire */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="text-purple-600" size={20} />
              <h2 className="text-lg font-semibold text-gray-900">Coûts par Partenaire</h2>
            </div>

            <div className="space-y-3">
              {coutsPartenaires.map((partenaire) => (
                <div
                  key={partenaire.partenaireId}
                  className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{partenaire.nom}</div>
                    <div className="text-sm text-gray-600">Volume: {partenaire.volumeTraite.toLocaleString()} CAD</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">{partenaire.fraisTotaux.toLocaleString()} CAD</div>
                    <div className="text-sm text-green-600">ROI: {partenaire.roi}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Évolution Bénéfice Net */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="text-orange-600" size={20} />
              <h2 className="text-lg font-semibold text-gray-900">Évolution du Bénéfice Net</h2>
            </div>

            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={evolutionBenefice}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="periode" />
                  <YAxis />
                  <Tooltip
                    formatter={(value) => [`${value} CAD`, "Montant"]}
                    labelFormatter={(label) => `Période: ${label}`}
                  />
                  <Bar dataKey="revenus" fill="#10b981" name="Revenus" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="beneficeNet" fill="#f59e0b" name="Bénéfice Net" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Section 5: Répartition Profits par Service */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <PieChart className="text-indigo-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-900">Répartition des Profits par Service</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 font-medium text-gray-600">Service</th>
                  <th className="text-right py-3 font-medium text-gray-600">Revenus</th>
                  <th className="text-right py-3 font-medium text-gray-600">Coûts</th>
                  <th className="text-right py-3 font-medium text-gray-600">Bénéfice Net</th>
                  <th className="text-right py-3 font-medium text-gray-600">Marge</th>
                </tr>
              </thead>
              <tbody>
                {profitsParService.map((service) => (
                  <tr key={service.serviceId} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 font-medium text-gray-900">{service.nom}</td>
                    <td className="py-3 text-right text-gray-900">{service.revenus.toLocaleString()} CAD</td>
                    <td className="py-3 text-right text-gray-900">{service.couts.toLocaleString()} CAD</td>
                    <td className="py-3 text-right text-gray-900">{service.beneficeNet.toLocaleString()} CAD</td>
                    <td className="py-3 text-right">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          service.marge > 15
                            ? "bg-green-100 text-green-800"
                            : service.marge > 5
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                        }`}
                      >
                        {service.marge}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Résumé Global */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Résumé Global</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <DollarSign className="mx-auto text-green-600 mb-2" size={24} />
              <div className="text-sm text-gray-600">Marge sur change</div>
              <div className="font-bold text-gray-900">{margeChange.margeMoyenne}%</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <CreditCard className="mx-auto text-blue-600 mb-2" size={24} />
              <div className="text-sm text-gray-600">Profit total change</div>
              <div className="font-bold text-gray-900">{margeChange.profitTotal.toLocaleString()} CAD</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Users className="mx-auto text-purple-600 mb-2" size={24} />
              <div className="text-sm text-gray-600">Commissions totales</div>
              <div className="font-bold text-gray-900">
                {beneficesNets.breakdown.commissionsTotales.toLocaleString()} CAD
              </div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Settings className="mx-auto text-orange-600 mb-2" size={24} />
              <div className="text-sm text-gray-600">Coûts fixes</div>
              <div className="font-bold text-gray-900">{beneficesNets.breakdown.coutsFixes.toLocaleString()} CAD</div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RevenusPage;
